/**
 * Format unifié du titre d'exercice : Compositeur – Œuvre, Mouvement.
 * Sans interprète/chef, avec mouvement abrégé si trop long.
 */

const MAX_TITLE_LENGTH = 90

/**
 * Retire un segment " - X / Y" ou " / Y" en fin de chaîne (chef, orchestre).
 */
function stripConductorSegment(s) {
  if (!s || typeof s !== 'string') return ''
  const t = s.trim()
  const lastSlash = t.match(/\s+[\/·]\s*[^/·]+$/)
  if (lastSlash) return t.slice(0, lastSlash.index).trim()
  const lastDash = t.match(/\s+[-–—]\s+[^-/·]+(?:\s*[\/·]\s*[^/·]+)?$/)
  if (lastDash && (lastDash[0].includes('/') || lastDash[0].includes('·'))) return t.slice(0, lastDash.index).trim()
  return t
}

/**
 * Abrège un libellé de mouvement pour réduire la longueur.
 */
function abbreviateMovement(movement) {
  if (!movement || typeof movement !== 'string') return ''
  let m = movement.trim()
  m = m.replace(/\bmouvement\b/gi, 'mvt')
  m = m.replace(/\b(2e|3e|4e)\s+mvt\b/gi, (_, n) => n + ' mvt')
  const tempoAbbrev = [
    [/\bAndante\b/gi, 'And.'],
    [/\bAllegro\b/gi, 'All.'],
    [/\bLarghetto\b/gi, 'Larg.'],
    [/\bAdagio\b/gi, 'Adag.'],
    [/\bScherzo\b/gi, 'Scherzo'],
    [/\bMolto\s+vivace\b/gi, 'Molto viv.'],
    [/\bAllegro\s+con\s+brio\b/gi, 'All. con brio'],
    [/\bVivace\b/gi, 'Viv.'],
    [/\bLargo\b/gi, 'Largo'],
    [/\bPresto\b/gi, 'Presto'],
    [/\bModerato\b/gi, 'Mod.'],
    [/\bLento\b/gi, 'Lento'],
  ]
  for (const [re, repl] of tempoAbbrev) {
    m = m.replace(re, repl)
  }
  if (m.length > 35) {
    const firstPart = m.match(/^([IVXLCDM]+\.?\s*[^.]+?)(?:\s*[.–]\s*|$)/i)
    if (firstPart) m = firstPart[1].trim() + (m.length > firstPart[1].length ? '…' : '')
  }
  return m
}

/**
 * Abrège le titre d'œuvre pour réduire la longueur (léger).
 */
function abbreviateWork(workTitle) {
  if (!workTitle || typeof workTitle !== 'string') return workTitle
  let w = workTitle
  w = w.replace(/\bSymphony\b/gi, 'Sym.')
  w = w.replace(/\bSymphonie\b/gi, 'Sym.')
  w = w.replace(/\bin\s+([A-G])\s+minor\b/gi, '$1 min.')
  w = w.replace(/\bin\s+([A-G])\s+major\b/gi, '$1 maj.')
  return w
}

/**
 * Construit le titre d'exercice formaté : Compositeur – Œuvre, Mouvement.
 * Exclut l'interprète, abrège le mouvement puis l'œuvre si trop long, tronque avec "…".
 *
 * @param {string} composer
 * @param {string} workTitle
 * @param {string} movementTitle - mouvement sélectionné (optionnel)
 * @returns {string}
 */
export function formatExerciseTitle(composer, workTitle, movementTitle) {
  const c = stripConductorSegment(composer || '').trim()
  const w = stripConductorSegment(workTitle || '').trim()
  const m = stripConductorSegment(movementTitle || '').trim()

  if (!c && !w && !m) return "Exercice d'analyse harmonique"

  const workPart = w || 'Œuvre'
  const base = c ? `${c} – ${workPart}` : workPart
  let movementPart = m

  let out = base
  if (movementPart) {
    out = `${base}, ${movementPart}`
  }

  if (out.length <= MAX_TITLE_LENGTH) return out

  movementPart = abbreviateMovement(movementPart)
  if (m) {
    out = `${base}, ${movementPart}`
    if (out.length <= MAX_TITLE_LENGTH) return out
  }

  const shortWork = abbreviateWork(w)
  const baseShort = c ? `${c} – ${shortWork}` : shortWork
  out = movementPart ? `${baseShort}, ${movementPart}` : baseShort
  if (out.length <= MAX_TITLE_LENGTH) return out

  if (out.length > MAX_TITLE_LENGTH) {
    out = out.slice(0, MAX_TITLE_LENGTH - 1).trim()
    if (out.endsWith(',')) out = out.slice(0, -1)
    out = out + '…'
  }
  return out
}
